// VishCoder Chat Hook - manages chat messages, formatting, and transmission logic
// Can be used across all tabs (Requirements, Architecture, Code, Documents, Preview)
import { useState, useEffect, useCallback, useRef } from 'react';
import {
    ChatMessage,
    ChatPage,
    ChatPayloadMessageAction,
    DiscoveryMessageMeta,
    PAYLOAD_MESSAGE_ACTIONS_MSG_TYPE,
    initializeChatHistory,
    getChatMessages,
    addChatMessage,
    addChatMessages,
    buildStreamingActivityChatMessages,
    setChatMessages,
    clearChatHistory,
    subscribeToChatMessages,
    pullLatestSessionMessagesForProject
} from './sharedChatService';
import { normalizeIndexPayload } from '../../../tabs/requirements/components/documents/phaseDocumentModel';
import { Attachment, ConversationalMessage } from './shared/attachements/conversationalTypes';
import { getRouter } from '../../../../../shared/lib/comm/gateway_ws/connection';
import { parseResponse } from '../../../../../shared/lib/comm/payloadHelpers';
import { useWebSocketStatus } from '../../../../../shared/lib/comm/gateway_ws/useWebSocketStatus';
import { TokenManager } from '../../../../../shared/lib/apiClient';

// Track pending requests by message ID to determine which page they belong to
let pendingRequests: Map<string, ChatPage | undefined> = new Map();
type ChatChannel = 'chat' | 'discovery';

function extractS3PathFromUrl(url: string): string | undefined {
    if (!url || (!url.startsWith('http://') && !url.startsWith('https://') && !url.startsWith('s3://'))) {
        return undefined;
    }

    if (url.startsWith('s3://')) {
        const raw = url.replace('s3://', '').trim();
        if (!raw) return undefined;
        const slashIdx = raw.indexOf('/');
        if (slashIdx >= 0) {
            const key = raw.slice(slashIdx + 1);
            return key ? decodeURIComponent(key) : undefined;
        }
        return undefined;
    }

    try {
        const parsed = new URL(url);
        const host = parsed.hostname;
        const pathname = parsed.pathname.replace(/^\/+/, '');

        if (!pathname) return undefined;

        // https://bucket.s3.us-east-1.amazonaws.com/<key>
        // https://bucket.s3.amazonaws.com/<key>
        // https://bucket.s3-accelerate.amazonaws.com/<key>
        if (host.includes('.s3.') || host.includes('.s3-')) {
            return decodeURIComponent(pathname);
        }

        // https://s3.us-east-1.amazonaws.com/<bucket>/<key>
        // https://s3.amazonaws.com/<bucket>/<key>
        if (host.startsWith('s3.')) {
            const slashIdx = pathname.indexOf('/');
            if (slashIdx < 0) return undefined;
            const key = pathname.slice(slashIdx + 1);
            return key ? decodeURIComponent(key) : undefined;
        }
    } catch {
        // Ignore invalid URL and fall back to regex path extraction below.
    }

    // Fallback parser for unknown but similar URL shapes.
    const match = url.match(/https?:\/\/[^/]+\/(.+)/);
    return match?.[1] ? decodeURIComponent(match[1]) : undefined;
}

function extractStructuredOutputAssistantText(rawToolUsageText: string): string | null {
    if (!rawToolUsageText || !rawToolUsageText.includes('StructuredOutput')) return null;
    const re = /"text"\s*:\s*"((?:\\.|[^"\\])*)"/g;
    let match: RegExpExecArray | null = null;
    let candidate: string | null = null;
    while ((match = re.exec(rawToolUsageText)) !== null) {
        candidate = match[1];
    }
    if (!candidate) return null;
    try {
        const decoded = JSON.parse(`"${candidate}"`) as string;
        const cleaned = decoded.trim();
        return cleaned.length > 0 ? cleaned : null;
    } catch {
        const cleaned = candidate.replace(/\\n/g, '\n').replace(/\\"/g, '"').trim();
        return cleaned.length > 0 ? cleaned : null;
    }
}

/** Optional flags for outbound send (e.g. discovery already showed the user bubble). */
export type SendMessageOptions = {
    /** Do not append another user row; use {@link existingUserMessageId} for correlation. */
    skipUserBubble?: boolean;
    /** Required when skipUserBubble is true: id of the existing user ChatMessage. */
    existingUserMessageId?: string;
    /**
     * Use this project id for persistence and outbound context when the hook's `projectId`
     * has not re-rendered yet (e.g. discovery "Proceed" right after POST /projects).
     */
    overrideProjectId?: string | number;
    /** Merged onto outbound `message` (e.g. `{ [actionId]: true }` for payload action clicks). */
    messageFieldExtensions?: Record<string, boolean>;
    /** Shown in the user bubble when `message` text is intentionally empty. */
    userBubbleDisplayText?: string;
};

function normalizeIncomingMessageActions(actions: unknown): ChatPayloadMessageAction[] | null {
    if (!Array.isArray(actions) || actions.length === 0) return null;
    const out: ChatPayloadMessageAction[] = [];
    for (const item of actions) {
        if (!item || typeof item !== 'object') continue;
        const a = item as Record<string, unknown>;
        const id = a.id != null && String(a.id).trim() !== '' ? String(a.id) : '';
        const label = a.label != null ? String(a.label) : '';
        if (!id || !label.trim()) continue;
        const style = typeof a.style === 'string' ? a.style : undefined;
        out.push(style !== undefined ? { id, label, style } : { id, label });
    }
    return out.length > 0 ? out : null;
}

/** Reads `message.actions` or top-level `actions` on a WS body / streaming row. */
function extractPayloadActionsFromWsMessage(bm: Record<string, unknown>): {
    actions: ChatPayloadMessageAction[] | null;
    text: string;
} {
    const nested = bm.message;
    if (nested && typeof nested === 'object') {
        const nm = nested as Record<string, unknown>;
        return {
            actions: normalizeIncomingMessageActions(nm.actions),
            text: nm.text == null ? '' : String(nm.text),
        };
    }
    return {
        actions: normalizeIncomingMessageActions(bm.actions),
        text: bm.text == null ? '' : String(bm.text),
    };
}
// Last page a message was sent from (used when response has no parentId/page)
let lastSentPageByChannel: Record<ChatChannel, ChatPage | undefined> = {
    chat: undefined,
    discovery: undefined
};

// Initialize WebSocket connection using router
const initializeWebSocket = () => {
    const router = getRouter();
    const state = router.getState();
    // Only connect if not already connected
    if (state !== 'connected') {
        router.connect();
    }
    return router.getConnection()?.websocket || null;
};

// Set up message handler for chat channel only
const initializedHandlers = new Set<ChatChannel>();
// Store current projectId in a ref that can be accessed by the message handler
let currentProjectIdRef: string | undefined = undefined;

/** After index/TOC arrives, re-pull session from Dynamo (debounced) so hydrate races do not drop the TOC. */
let discoveryPullLatestTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleDiscoveryPullLatestAfterIndex(ichannel: ChatChannel) {
    if (ichannel !== 'discovery') return;
    if (discoveryPullLatestTimer) clearTimeout(discoveryPullLatestTimer);
    discoveryPullLatestTimer = setTimeout(() => {
        discoveryPullLatestTimer = null;
        const pid = currentProjectIdRef;
        if (pid) {
            pullLatestSessionMessagesForProject(pid).catch((e) =>
                console.error('pullLatestSessionMessagesForProject failed:', e)
            );
        }
    }, 450);
}

const setMessageHandlerProjectId = (projectId?: string | number | null) => {
    currentProjectIdRef = projectId ? String(projectId) : undefined;
};

const initializeMessageHandler = (channel: ChatChannel) => {
    if (initializedHandlers.has(channel)) return;
    initializedHandlers.add(channel);

    const router = getRouter();
    router.setChannelHandler(channel, (payload) => {
        try {
            // Only process messages from this handler's channel
            if (payload.channel && payload.channel !== channel) {
                return; // Ignore messages from other channels
            }

            // Log full payload from backend for debugging
            console.log('📥 [Chat] Full VishMakerPayload from backend:', JSON.stringify(payload, null, 2));
            
            // Parse the payload: backend sends full VishMakerPayload (v, id, actor, channel, body: { kind, payload }).
            // Try full payload first so parseResponse gets body.kind and body.payload.
            const data = payload.body?.payload || payload;

            if ((data as any)?.kind === 'status' || (typeof (data as any)?.message === 'string' && channel === 'discovery')) {
                return;
            }

            let parsedResponse = parseResponse(payload);
            if (!parsedResponse) {
                parsedResponse = parseResponse(data);
            }
            // Fallback when body.payload is raw { message: { text, type, attachments } } (no top-level v/type)
            if (!parsedResponse && data && typeof data === 'object' && data.message && typeof (data.message as any).text === 'string') {
                parsedResponse = {
                    type: 'message',
                    actor: (payload as any).actor || 'Coder',
                    status: 'completed',
                    body: data
                };
            }

            if (!parsedResponse) {
                console.error('Invalid response format from VishCoder:', data);
                return;
            }

            if (parsedResponse.type === 'heartbeat') {
                return; // Ignore heartbeats
            }

            // Handle error responses (status: "error", body.kind: "error", body.payload.error)
            if (parsedResponse.type === 'error' || (payload as any).status === 'error') {
                const errorPayload = parsedResponse.body;
                const errorText = typeof errorPayload?.error === 'string'
                    ? errorPayload.error
                    : (errorPayload?.message ?? 'An error occurred while processing your message.');
                const responsePage = (payload as any).origin?.page ?? lastSentPageByChannel[channel] ?? (channel === 'discovery' ? 'project_chat' : 'requirements');
                const errorMessage: ChatMessage = {
                    id: `error-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
                    type: 'assistant',
                    content: `❌ ${errorText}`,
                    timestamp: new Date(),
                    isContract: false,
                    page: responsePage as ChatPage,
                    channel
                };
                addChatMessage(errorMessage);
            }

            // Handle question_to_ai messages (for requirements tab)
            if (parsedResponse.type === 'question_to_ai') {
                if (parsedResponse.actor === 'Coder') {
                    const messages = parsedResponse.body.messages || {};
                    // Check if messages is an object (not an array) before accessing properties
                    const messagesObj = Array.isArray(messages) ? {} : messages;
                    const responseText = messagesObj?.response_text || 
                                       messagesObj?.response || 
                                       'Received response from VishCoder AI Agent';
                    
                    const aiResponse: ChatMessage = {
                        id: `ai-response-${Date.now()}`,
                        type: 'assistant',
                        content: responseText,
                        timestamp: new Date(),
                        isContract: false,
                        page: 'requirements', // Tag as requirements response
                        channel
                    };
                    
                    addChatMessage(aiResponse, currentProjectIdRef);
                }
            }

            // Handle conversational_chat messages (for other tabs)
            if (parsedResponse.type === 'conversational_chat' as any) {
                if (parsedResponse.actor === 'Coder') {
                    // Try multiple possible locations for the conversational message
                    let responseData: any = null;
                    
                    // Check body.message first (most likely location)
                    const body = parsedResponse.body as any;
                    if (body?.message) {
                        responseData = body.message;
                    }
                    // Check body.messages (alternative location)
                    else if (body?.messages) {
                        // If messages is an object, try to extract the message
                        if (typeof body.messages === 'object' && !Array.isArray(body.messages)) {
                            responseData = body.messages.message || body.messages;
                        } else {
                            responseData = body.messages;
                        }
                    }
                    // Check body directly
                    else if (body) {
                        responseData = body;
                    }
                    // Fallback to entire response
                    else {
                        responseData = parsedResponse;
                    }
                    
                    console.log('📦 Conversational response data:', JSON.stringify(responseData, null, 2));
                    
                    const responseId = responseData.id || `ai-${Date.now()}`;
                    const parentId = responseData.parentId;
                    
                    // Get page from pending request if we have parentId
                    let responsePage: ChatPage | undefined = undefined;
                    if (parentId) {
                        responsePage = pendingRequests.get(parentId);
                        pendingRequests.delete(parentId); // Clean up
                    }
                    
                    // Parse blocks - handle different possible structures
                    let blocks: any[] = [];
                    if (responseData.blocks) {
                        if (Array.isArray(responseData.blocks)) {
                            blocks = responseData.blocks;
                        } else if (typeof responseData.blocks === 'object') {
                            // If blocks is an object, try to extract array from it
                            blocks = responseData.blocks.items || responseData.blocks.blocks || [];
                        }
                    }
                    
                    console.log('🧱 Parsed blocks:', blocks);
                    
                    // Parse conversational response format
                    // Get source/page from response if available, otherwise use tracked page
                    const responseSource = responseData.source || responsePage;
                    
                    const conversationalResponse: ConversationalMessage = {
                        v: responseData.v || 1,
                        id: responseId,
                        parentId: parentId || null,
                        from: 'ai',
                        type: responseData.type || 'answer',
                        text: responseData.text || 'Received response from VishCoder AI Agent',
                        source: responseSource, // Include source from response or tracked page
                        render: responseData.render || 'card',
                        blocks: blocks
                    };

                    // Use source from response if available, otherwise use tracked page
                    const finalPage = responseSource || responsePage;

                    const aiResponse: ChatMessage = {
                        id: responseId,
                        type: 'assistant',
                        content: conversationalResponse.text,
                        timestamp: new Date(),
                        isContract: false,
                        page: finalPage, // Tag with page from response source or tracked request
                    channel,
                        conversational: conversationalResponse,
                        blocks: conversationalResponse.blocks
                    };
                    
                    console.log('✅ Created AI response with blocks:', {
                        id: aiResponse.id,
                        blocksCount: aiResponse.blocks?.length || 0,
                        blocks: aiResponse.blocks
                    });
                    
                    addChatMessage(aiResponse, currentProjectIdRef);
                }
            }

            // Handle build_feature messages (status updates, contract status, PR info, etc.)
            if (parsedResponse.type === 'build_feature' && parsedResponse.actor === 'Coder') {
                // Handle contract status (InProgress, Completed, Failed)
                if (parsedResponse.status === 'InProgress') {
                    const contractReceivedMessage: ChatMessage = {
                        id: `contract-received-${Date.now()}`,
                        type: 'assistant',
                        content: '📥 Build feature request received by VishCoder AI Agent. Processing started...',
                        timestamp: new Date(),
                        isContract: false,
                        page: 'requirements',
                        channel
                    };
                    addChatMessage(contractReceivedMessage, currentProjectIdRef);
                } else if (parsedResponse.status === 'Completed') {
                    const contractCompletedMessage: ChatMessage = {
                        id: `contract-completed-${Date.now()}`,
                        type: 'assistant',
                        content: '✅ Build feature processing completed by VishCoder AI Agent!',
                        timestamp: new Date(),
                        isContract: false,
                        page: 'requirements',
                        channel
                    };
                    addChatMessage(contractCompletedMessage, currentProjectIdRef);
                } else if (parsedResponse.status === 'Failed' || parsedResponse.status === 'Error') {
                    const messages = parsedResponse.body.messages || {};
                    const messagesObj = Array.isArray(messages) ? {} : messages;
                    const errorMsg = messagesObj?.error || messagesObj?.message || 'Unknown error';
                    
                    const contractErrorMessage: ChatMessage = {
                        id: `contract-error-${Date.now()}`,
                        type: 'assistant',
                        content: `❌ Build feature processing failed: ${errorMsg}`,
                        timestamp: new Date(),
                        isContract: false,
                        page: 'requirements',
                        channel
                    };
                    addChatMessage(contractErrorMessage, currentProjectIdRef);
                }
                
                // Handle status_details from build_feature
                if (parsedResponse.body.contract?.statusDetails) {
                    const statusDetails = parsedResponse.body.contract.statusDetails;
                    const agent = statusDetails.agent || 'Unknown Agent';
                    const llm = statusDetails.LLM || 'Unknown LLM';
                    const details = statusDetails.details || 'No details provided';
                    const progress = statusDetails.progress || null;
                    
                    let detailsString: string;
                    if (typeof details === 'object' && details !== null) {
                        if (details.file_name && details.file_type) {
                            detailsString = `${details.file_type} file: ${details.file_name}`;
                            if (details.file_size) {
                                detailsString += ` (${details.file_size} bytes)`;
                            }
                        } else {
                            detailsString = JSON.stringify(details);
                        }
                    } else {
                        detailsString = String(details);
                    }
                    
                    // Skip heartbeat-related messages
                    if (detailsString.toLowerCase().includes('heartbeat') || 
                        detailsString.toLowerCase().includes('main websocket connection')) {
                        return;
                    }
                    
                    const isManagerInitial = agent === 'Manager' && detailsString.includes('Thanks for sending the request');
                    
                    const statusUpdateMessage: ChatMessage = {
                        id: `status-update-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
                        type: isManagerInitial ? 'assistant' : 'status',
                        content: isManagerInitial ? `${agent}(${llm}) : ${detailsString}` : detailsString,
                        agent: isManagerInitial ? undefined : agent,
                        llm: isManagerInitial ? undefined : llm,
                        progress: progress,
                        timestamp: new Date(),
                        isContract: false,
                        page: 'requirements',
                        channel
                    };
                    
                    addChatMessage(statusUpdateMessage, currentProjectIdRef);
                }
            }
            
            // Handle status_update messages
            if (parsedResponse.type === 'status_update' && parsedResponse.actor === 'Coder') {
                if (parsedResponse.body.statusDetails) {
                    const statusDetails = parsedResponse.body.statusDetails;
                    const agent = statusDetails.agent || 'Unknown Agent';
                    const llm = statusDetails.LLM || 'Unknown LLM';
                    const details = statusDetails.details || 'No details provided';
                    const progress = statusDetails.progress || null;
                    
                    let detailsString: string;
                    if (typeof details === 'object' && details !== null) {
                        const detailsObj = details as Record<string, any>;
                        if (detailsObj.file_name && detailsObj.file_type) {
                            detailsString = `${detailsObj.file_type} file: ${detailsObj.file_name}`;
                            if (detailsObj.file_size) {
                                detailsString += ` (${detailsObj.file_size} bytes)`;
                            }
                        } else {
                            detailsString = JSON.stringify(details);
                        }
                    } else {
                        detailsString = String(details);
                    }
                    
                    // Skip heartbeat-related messages
                    if (detailsString.toLowerCase().includes('heartbeat') || 
                        detailsString.toLowerCase().includes('main websocket connection')) {
                        return;
                    }
                    
                    const statusUpdateMessage: ChatMessage = {
                        id: `status-update-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
                        type: 'status',
                        content: detailsString,
                        agent: agent,
                        llm: llm,
                        progress: progress,
                        timestamp: new Date(),
                        isContract: false,
                        page: 'requirements',
                        channel
                    };
                    
                    addChatMessage(statusUpdateMessage, currentProjectIdRef);
                }
            }
            
            // Handle clarification_needed_from_user messages
            if (parsedResponse.type === 'clarification_needed_from_user' && parsedResponse.actor === 'Coder') {
                const messages = parsedResponse.body.messages || {};
                const messagesObj = Array.isArray(messages) ? {} : messages;
                const clarificationText = messagesObj?.question_text || 'Clarification needed from user';
                
                const clarificationMessage: ChatMessage = {
                    id: `clarification-${Date.now()}`,
                    type: 'assistant',
                    content: `❓ Clarification needed: ${clarificationText}`,
                    timestamp: new Date(),
                    isContract: false,
                    page: 'requirements',
                        channel
                };
                
                addChatMessage(clarificationMessage, currentProjectIdRef);
            }

            // VishMaker v1: body.kind "messages" → body.payload.messages[] (streaming thinking/tool_usage/text)
            const bodyMessages: unknown[] | undefined = Array.isArray(parsedResponse.body?.messages)
                ? parsedResponse.body.messages
                : undefined;
            const messageActorsOk =
                parsedResponse.actor === 'Coder' ||
                parsedResponse.actor === 'coder' ||
                parsedResponse.actor === 'system' ||
                parsedResponse.actor === 'System';

            if (
                parsedResponse.type === 'messages' &&
                bodyMessages &&
                bodyMessages.length > 0 &&
                messageActorsOk
            ) {
                const responsePage = ((payload as any).origin?.page
                    ?? lastSentPageByChannel[channel]
                    ?? (channel === 'discovery' ? 'project_chat' : 'requirements')) as ChatPage;
                const backendPayloadId = String((payload as any).id ?? `local-${Date.now()}`);
                const backendTs = (payload as any).ts as string | undefined;

                const toAppend: ChatMessage[] = [];
                bodyMessages.forEach((raw, idx) => {
                    if (!raw || typeof raw !== 'object') return;
                    const bm = raw as Record<string, unknown>;
                    const mt = typeof bm.type === 'string' ? bm.type : 'text';
                    const text = bm.text == null ? '' : String(bm.text);

                    const { actions: payloadActions, text: payloadText } = extractPayloadActionsFromWsMessage(bm);
                    if (payloadActions?.length) {
                        toAppend.push({
                            id: `${backendPayloadId}-idx-${idx}-actions`,
                            type: 'assistant',
                            content: payloadText,
                            msgType: PAYLOAD_MESSAGE_ACTIONS_MSG_TYPE,
                            message_actions: payloadActions,
                            timestamp: backendTs ? new Date(backendTs) : new Date(),
                            isContract: false,
                            page: responsePage,
                            channel,
                            backend_payload_id: backendPayloadId,
                            backend_ts: backendTs,
                            ...(currentProjectIdRef ? { project_id: currentProjectIdRef } : {}),
                        });
                        return;
                    }

                    if (mt === 'index') {
                        const index_value = normalizeIndexPayload(bm.value);
                        const discovery: DiscoveryMessageMeta = {
                            msg_type: 'index',
                            index_value,
                        };
                        toAppend.push({
                            id: `${backendPayloadId}-idx-${idx}-index`,
                            type: 'assistant',
                            content: text,
                            msgType: 'index',
                            discovery,
                            timestamp: backendTs ? new Date(backendTs) : new Date(),
                            isContract: false,
                            page: responsePage,
                            channel,
                            backend_payload_id: backendPayloadId,
                            backend_ts: backendTs,
                        });
                        return;
                    }

                    if (mt === 'doc_summary') {
                        const phase = String(bm.phase ?? '').trim();
                        const slot = String(bm.slot ?? '').trim();
                        const discovery: DiscoveryMessageMeta = {
                            msg_type: 'doc_summary',
                            phase,
                            slot,
                        };
                        toAppend.push({
                            id: `${backendPayloadId}-idx-${idx}-doc`,
                            type: 'assistant',
                            content: text,
                            msgType: 'doc_summary',
                            discovery,
                            timestamp: backendTs ? new Date(backendTs) : new Date(),
                            isContract: false,
                            page: responsePage,
                            channel,
                            backend_payload_id: backendPayloadId,
                            backend_ts: backendTs,
                        });
                        return;
                    }

                    const streamingBatch = buildStreamingActivityChatMessages([raw], {
                        backend_payload_id: `${backendPayloadId}-m${idx}`,
                        backend_ts: backendTs,
                        page: responsePage,
                        channel,
                    });
                    if (streamingBatch.length) toAppend.push(...streamingBatch);
                    else if (mt !== 'thinking' && mt !== 'tool_usage' && mt !== 'text') {
                        const discovery: DiscoveryMessageMeta | undefined =
                            channel === 'discovery' ? { msg_type: mt } : undefined;
                        toAppend.push({
                            id: `${backendPayloadId}-idx-${idx}-${mt}`,
                            type: 'assistant',
                            content: text,
                            msgType: mt,
                            discovery,
                            timestamp: backendTs ? new Date(backendTs) : new Date(),
                            isContract: false,
                            page: responsePage,
                            channel,
                            backend_payload_id: backendPayloadId,
                            backend_ts: backendTs,
                        });
                    }
                });

                const hasRenderableAssistant = toAppend.some(
                    (m) => m.type === 'assistant' && !m.stream_activity_category
                );
                if (!hasRenderableAssistant && channel === 'discovery') {
                    for (let idx = bodyMessages.length - 1; idx >= 0; idx--) {
                        const raw = bodyMessages[idx];
                        if (!raw || typeof raw !== 'object') continue;
                        const bm = raw as Record<string, unknown>;
                        const mt = typeof bm.type === 'string' ? bm.type : '';
                        if (mt !== 'tool_usage') continue;
                        const fallbackText = extractStructuredOutputAssistantText(
                            String(bm.text ?? '')
                        );
                        if (!fallbackText) continue;
                        toAppend.push({
                            id: `${backendPayloadId}-idx-${idx}-structured-text`,
                            type: 'assistant',
                            content: fallbackText,
                            msgType: 'text',
                            timestamp: backendTs ? new Date(backendTs) : new Date(),
                            isContract: false,
                            page: responsePage,
                            channel,
                            backend_payload_id: backendPayloadId,
                            backend_ts: backendTs,
                        });
                        break;
                    }
                }

                if (toAppend.length) {
                    addChatMessages(toAppend, currentProjectIdRef);
                    if (toAppend.some((m) => m.msgType === 'index')) {
                        scheduleDiscoveryPullLatestAfterIndex(channel);
                    }
                }
                return;
            }

            // Legacy single { message: { text, type, ... } } (body.kind "message" or parsed body.message)
            const bodyMessage = parsedResponse.body?.message;
            const isMessageKind = parsedResponse.type === 'message' || parsedResponse.type === 'messages';
            if (isMessageKind && bodyMessage && messageActorsOk) {
                const bm = bodyMessage as Record<string, unknown>;
                const mt = typeof bm.type === 'string' ? bm.type : 'text';
                const text = bm.text == null ? '' : String(bm.text);

                const responsePage = (payload as any).origin?.page
                    ?? (bm as any).source
                    ?? (bm as any).page
                    ?? lastSentPageByChannel[channel]
                    ?? (channel === 'discovery' ? 'project_chat' : 'requirements');

                const id = `ai-response-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
                const { actions: singlePayloadActions, text: singlePayloadText } =
                    extractPayloadActionsFromWsMessage(bm);

                if (singlePayloadActions?.length) {
                    const stableId = String((payload as any).id ?? id);
                    addChatMessage(
                        {
                            id: stableId,
                            type: 'assistant',
                            content: singlePayloadText,
                            msgType: PAYLOAD_MESSAGE_ACTIONS_MSG_TYPE,
                            message_actions: singlePayloadActions,
                            timestamp: new Date(),
                            isContract: false,
                            page: responsePage as ChatPage,
                            channel,
                            ...(currentProjectIdRef ? { project_id: currentProjectIdRef } : {}),
                        },
                        currentProjectIdRef
                    );
                    return;
                }

                if (mt === 'index') {
                    const index_value = normalizeIndexPayload(bm.value);
                    const discovery: DiscoveryMessageMeta = {
                        msg_type: 'index',
                        index_value,
                    };
                    addChatMessage(
                        {
                            id,
                            type: 'assistant',
                            content: text,
                            msgType: 'index',
                            discovery,
                            timestamp: new Date(),
                            isContract: false,
                            page: responsePage as ChatPage,
                            channel,
                        },
                        currentProjectIdRef
                    );
                    scheduleDiscoveryPullLatestAfterIndex(channel);
                    return;
                }

                if (mt === 'doc_summary') {
                    const phase = String(bm.phase ?? '').trim();
                    const slot = String(bm.slot ?? '').trim();
                    const discovery: DiscoveryMessageMeta = {
                        msg_type: 'doc_summary',
                        phase,
                        slot,
                    };
                    addChatMessage(
                        {
                            id,
                            type: 'assistant',
                            content: text,
                            msgType: 'doc_summary',
                            discovery,
                            timestamp: new Date(),
                            isContract: false,
                            page: responsePage as ChatPage,
                            channel,
                        },
                        currentProjectIdRef
                    );
                    return;
                }

                const streamingSingle = buildStreamingActivityChatMessages([bm], {
                    backend_payload_id: String((payload as any).id ?? id),
                    backend_ts: (payload as any).ts as string | undefined,
                    page: responsePage as ChatPage,
                    channel,
                });
                if (streamingSingle.length) {
                    addChatMessages(streamingSingle, currentProjectIdRef);
                    return;
                }

                const discovery: DiscoveryMessageMeta | undefined =
                    channel === 'discovery'
                        ? { msg_type: mt }
                        : undefined;

                addChatMessage(
                    {
                        id,
                        type: 'assistant',
                        content: text,
                        msgType: mt,
                        discovery,
                        timestamp: new Date(),
                        isContract: false,
                        page: responsePage as ChatPage,
                        channel,
                    },
                    currentProjectIdRef
                );
            }
        } catch (error) {
            console.error('Error processing WebSocket message:', error);
        }
    });
};


export const useVishCoderChat = (
    page?: ChatPage,
    projectId?: string | number | null,
    channel: ChatChannel = 'chat'
) => {
    // Use shared WebSocket connection status hook
    const wsStatus = useWebSocketStatus();
    
    // Initialize chat history with project (creates/loads session)
    useEffect(() => {
        if (projectId) {
            setMessageHandlerProjectId(projectId);
            initializeChatHistory(projectId);
        } else {
            setMessageHandlerProjectId(null);
            initializeChatHistory();
        }
    }, [projectId]);

    const [chatMessages, setChatMessages] = useState<ChatMessage[]>(getChatMessages(page, channel));
    const [agentProcessing, setAgentProcessing] = useState<boolean>(false);

    // Subscribe to chat message changes from shared service (filtered by page and channel='chat')
    useEffect(() => {
        const unsubscribe = subscribeToChatMessages((messages) => {
            setChatMessages([...messages]);
        }, page, channel);
        return unsubscribe;
    }, [page, channel]);

    // Initialize message handler and WebSocket connection
    useEffect(() => {
        // Initialize message handler
        initializeMessageHandler(channel);
        
        // Initialize WebSocket connection
        initializeWebSocket();
    }, [channel]);

    // Send message to VishCoder (with optional attachments for conversational tabs)
    // NOTE: Conversational schema is ONLY for non-requirements tabs (architecture, code, documents, preview)
    // Requirements tab uses the legacy question_to_ai format
    const sendMessage = useCallback((message: string, attachments?: Attachment[], options?: SendMessageOptions) => {
        const hasFieldExtensions =
            options?.messageFieldExtensions &&
            Object.keys(options.messageFieldExtensions).length > 0;
        if (
            !message.trim() &&
            (!attachments || attachments.length === 0) &&
            !hasFieldExtensions
        ) {
            return;
        }

        const effectiveProjectId =
            options?.overrideProjectId !== undefined && options.overrideProjectId !== null
                ? options.overrideProjectId
                : projectId;

        const skipUserBubble =
            Boolean(options?.skipUserBubble) &&
            typeof options?.existingUserMessageId === 'string' &&
            options.existingUserMessageId.length > 0;
        const messageId = skipUserBubble ? options!.existingUserMessageId! : `msg_${Date.now()}`;
        const timestamp = new Date();

        // Backend expects each attachment as an object (e.g. .get("path")), not a bare string.
        const attachmentRefs: Array<{
            kind: 'image';
            path: string;
            url?: string;
            mime?: string;
            name?: string;
        }> = [];
        if (attachments && attachments.length > 0) {
            attachments.forEach(att => {
                if (att.kind !== 'image') return;
                let path = att.path;
                if (!path && att.url) {
                    path = extractS3PathFromUrl(att.url);
                }
                if (path) {
                    attachmentRefs.push({
                        kind: 'image',
                        path,
                        url: att.url,
                        mime: att.mime,
                        name: att.name
                    });
                } else {
                    console.warn('⚠️ Skipping attachment without usable S3 path:', {
                        name: att.name,
                        url: att.url,
                        hasPreviewUrl: Boolean(att.previewUrl)
                    });
                }
            });
        }

        if (attachmentRefs.length > 0) {
            console.log('📎 [Chat] Attachment refs prepared for outbound payload:', attachmentRefs.map((att) => ({
                kind: att.kind,
                name: att.name,
                path: att.path,
                has_url: Boolean(att.url)
            })));
        } else if (attachments && attachments.length > 0) {
            console.warn('⚠️ [Chat] Attachments were selected but none had a usable S3 path for outbound payload.');
        }

        // Determine message type based on content
        const messageType = attachments && attachments.length > 0 ? 'text_with_attachments' : 'text';

        // Create message payload in the required format for outbound module:
        // channel: 'chat', status: 'streaming', kind: 'message'
        // payload: { message: { text: ..., type: ..., attachments: [{ kind, path, url?, ... }] } }
        const messagePayload = {
            message: {
                text: message || '',
                type: messageType,
                attachments: attachmentRefs,
                ...(options?.messageFieldExtensions ?? {}),
            },
        };

        // Create user message for UI display
        const projectIdToUse =
            effectiveProjectId !== undefined && effectiveProjectId !== null
                ? String(effectiveProjectId)
                : undefined;
        const bubbleText =
            options?.userBubbleDisplayText ??
            (message ||
                (attachments && attachments.length > 0 ? `[${attachments.length} attachment(s)]` : ''));
        const outboundUserId = TokenManager.getUserId();
        const userMessage: ChatMessage = {
            id: messageId,
            type: 'user',
            content: bubbleText,
            timestamp,
            isContract: false,
            page: page,
            channel,
            attachments: attachments, // Store attachments for display
            ...(outboundUserId ? { backend_user_id: outboundUserId } : {}),
        };
        if (!skipUserBubble) {
            addChatMessage(userMessage, projectIdToUse);
        }

        const router = getRouter();
        const comm = router.getCommunicationManager();
        
        if (wsStatus.isConnected && comm) {
            setAgentProcessing(true);
            
            // Set up channel context for chat channel (include userId and projectId for backend)
            const contextMessageId = crypto.randomUUID();
            const contextTimestamp = new Date().toISOString();
            const threadId = `thread_chat_${Date.now()}`;
            const userId = TokenManager.getUserId();
            router.setChannelContext(channel, {
                threadId,
                originMessageId: contextMessageId,
                originActor: 'user',
                origints: contextTimestamp,
                page: page,
                ...(userId && { userId }),
                ...(effectiveProjectId != null &&
                    effectiveProjectId !== undefined && { projectId: String(effectiveProjectId) })
            }, contextMessageId, 'user', contextTimestamp);
            
            // Track this request so we can tag the response with the correct page
            pendingRequests.set(messageId, page);
            lastSentPageByChannel[channel] = page;
            
            // Log outbound chat payload for debugging
            console.log('📤 [Chat] Outbound payload (body.payload):', JSON.stringify(messagePayload, null, 2));
            console.log('📤 [Chat] emit args: channel=chat, actor=user, status=streaming, kind=message, messageType=request');
            
            // Send using outbound module with required format:
            // channel: 'chat', status: 'streaming', kind: 'message'
            const success = comm.emit(channel, 'user', 'streaming', 'message', messagePayload, 'request');
            
            if (!success) {
                console.error('Failed to send message via outbound');
                const errorMessage: ChatMessage = {
                    id: `error-${Date.now()}`,
                    type: 'assistant',
                    content: '❌ Failed to send message. Please try again.',
                    timestamp: new Date(),
                    isContract: false,
                    page: page,
                    channel
                };
                addChatMessage(errorMessage);
            }
            
            setTimeout(() => {
                setAgentProcessing(false);
            }, 1000);
        } else {
            // Try to connect if not connected
            initializeWebSocket();
            console.error('WebSocket not connected');
            const errorMessage: ChatMessage = {
                id: `error-${Date.now()}`,
                type: 'assistant',
                content: '❌ Not connected to VishCoder AI Agent. Please wait for connection...',
                timestamp: new Date(),
                isContract: false,
                page: page,
                channel
            };
            addChatMessage(errorMessage, projectIdToUse);
        }
    }, [channel, page, projectId, wsStatus.isConnected]);

    // Clear chat history (for current page only)
    const clearChatHistoryCallback = useCallback(() => {
        clearChatHistory(page);
    }, [page]);

    // Get agent socket (for sending contracts, etc.)
    // Use ref to cache the socket and avoid re-initializing on every call
    const agentSocketRef = useRef<WebSocket | null>(null);
    
    const getAgentSocket = useCallback(() => {
        const router = getRouter();
        const conn = router.getConnection();
        const socket = conn?.websocket || null;
        
        // Update ref if socket exists
        if (socket) {
            agentSocketRef.current = socket;
        }
        
        // Only initialize if we don't have a socket and WebSocket is not disconnected
        if (!socket && !wsStatus.isDisconnected) {
            const newSocket = initializeWebSocket();
            if (newSocket) {
                agentSocketRef.current = newSocket;
            }
        }
        
        return agentSocketRef.current;
    }, [wsStatus.isDisconnected]);

    return {
        chatMessages,
        agentConnected: wsStatus.isConnected,
        agentProcessing,
        sendMessage,
        clearChatHistory: clearChatHistoryCallback,
        getAgentSocket
    };
};

export { markChatMessageActionsUsed, PAYLOAD_MESSAGE_ACTIONS_MSG_TYPE, type ChatPayloadMessageAction } from './sharedChatService';
